import { getCommonPrintStyles, getHeaderHtml, getFooterHtml, getSignatureHtml } from "./printStyles";
import { downloadAsPDF, createPrintDocument } from "./printingBridge";
import { enhancedToast } from "@/utils/notifications/enhancedToast";

const TEACHERS_PER_PAGE = 12;

interface TeacherData {
  id: string;
  name: string;
  department: string;
  position: string;
  email: string;
  phone: string;
  qualifications: string[];
  joinDate: string;
  status: string;
}

/**
 * Export teachers list as PDF with pagination support
 */
export const printTeachersListPaginatedAsPDF = async (
  teachers: TeacherData[],
  startPage: number = 1,
  endPage: number = 1
) => {
  try {
    console.log('Starting teachers list PDF download...', { teachers: teachers.length, startPage, endPage });
    
    // Show loading toast
    const loadingToast = enhancedToast.loading('Generating teachers list PDF...');
    
    const totalPages = Math.ceil(teachers.length / TEACHERS_PER_PAGE);
    const actualEndPage = Math.min(endPage, totalPages);
    const actualStartPage = Math.max(1, startPage);
    
    console.log('Calculated pages:', { totalPages, actualStartPage, actualEndPage });
    
    // Calculate which teachers to include
    const startIndex = (actualStartPage - 1) * TEACHERS_PER_PAGE;
    const endIndex = actualEndPage * TEACHERS_PER_PAGE;
    const teachersToShow = teachers.slice(startIndex, endIndex);
    
    console.log('Teachers to show:', { startIndex, endIndex, count: teachersToShow.length });
    
    // Group teachers by pages
    const pages: TeacherData[][] = [];
    for (let i = 0; i < teachersToShow.length; i += TEACHERS_PER_PAGE) {
      pages.push(teachersToShow.slice(i, i + TEACHERS_PER_PAGE));
    }
    
    // Generate content for all pages using standardized structure
    const allPagesContent = pages.map((pageTeachers, pageIndex) => {
      const currentPageNumber = actualStartPage + pageIndex;
      
      return `
        <div class="print-page ${pageIndex > 0 ? 'page-break' : ''}">
          ${getHeaderHtml(`Teachers List - Page ${currentPageNumber} of ${totalPages}`)}
          
          <div class="print-content">
            <div class="page-info">
              <strong>Page ${currentPageNumber} of ${totalPages}</strong> | 
              Teachers ${(currentPageNumber - 1) * TEACHERS_PER_PAGE + 1} - ${Math.min(currentPageNumber * TEACHERS_PER_PAGE, teachers.length)} of ${teachers.length}
            </div>
            
            <table>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Name</th>
                  <th>Department</th>
                  <th>Position</th>
                  <th>Contact</th>
                  <th>Qualifications</th>
                  <th>Join Date</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                ${pageTeachers.map((teacher, index) => {
                  const displayId = (startIndex + (pageIndex * TEACHERS_PER_PAGE) + index + 1).toString();
                  const safeStatus = teacher.status || 'Active';
                  const statusClass = safeStatus.toLowerCase().replace(/\s+/g, '-');
                  
                  return `
                    <tr>
                      <td>${displayId}</td>
                      <td>${teacher.name || 'N/A'}</td>
                      <td>${teacher.department || 'N/A'}</td>
                      <td>${teacher.position || 'N/A'}</td>
                      <td>
                        <div>${teacher.email || 'N/A'}</div>
                        <div style="font-size: 0.8em; color: #666;">${teacher.phone || 'N/A'}</div>
                      </td>
                      <td>${teacher.qualifications.length > 0 ? teacher.qualifications.join(', ') : 'N/A'}</td>
                      <td>${teacher.joinDate || 'N/A'}</td>
                      <td class="status-${statusClass}">${safeStatus}</td>
                    </tr>
                  `;
                }).join('')}
              </tbody>
            </table>
            
            ${getSignatureHtml('teacher')}
          </div>
          
          ${getFooterHtml('teacher')}
        </div>
      `;
    }).join('');

    const styles = `
      ${getCommonPrintStyles()}
      .page-info {
        text-align: center;
        font-size: 0.9em;
        color: #666;
        margin: 10px 0;
        padding: 8px;
        background: #f8f9fa;
        border-radius: 4px;
      }
      table {
        width: 100%;
        border-collapse: collapse;
        margin-top: 16px;
        font-size: 0.8em;
      }
      th, td {
        padding: 8px 4px;
        border-bottom: 1px solid #ddd;
        text-align: left;
        vertical-align: top;
      }
      th {
        background: #f5f5f5;
        font-weight: bold;
        border-bottom: 2px solid #ccc;
        font-size: 0.85em;
      }
      .status-active {
        color: #2e7d32;
        font-weight: 500;
      }
      .status-full-time {
        color: #1976d2;
        font-weight: 500;
      }
      .status-part-time {
        color: #ed6c02;
        font-weight: 500;
      }
      .status-inactive {
        color: #d32f2f;
        font-weight: 500;
      }
    `;

    // Generate the complete print document
    const printDocument = createPrintDocument(allPagesContent, 'Teachers_List_Paginated_PDF', styles);
    
    // Create filename
    const filename = actualStartPage === actualEndPage 
      ? `Teachers_List_Page_${actualStartPage}_${new Date().toISOString().split('T')[0]}`
      : `Teachers_List_Pages_${actualStartPage}-${actualEndPage}_${new Date().toISOString().split('T')[0]}`;
    
    console.log('Starting PDF download with filename:', filename);
    
    // Download as PDF directly
    await downloadAsPDF(printDocument, filename, {
      onError: (error) => {
        console.error('PDF download error:', error);
        enhancedToast.dismiss(loadingToast);
        enhancedToast.error('Failed to download teachers list');
      }
    });
    
    // Dismiss loading toast and show success with download sound
    enhancedToast.dismiss(loadingToast);
    const pageText = actualStartPage === actualEndPage ? `page ${actualStartPage}` : `pages ${actualStartPage}-${actualEndPage}`;
    enhancedToast.download(`Teachers list ${pageText} downloaded as PDF!`);
    
  } catch (error) {
    console.error('Error downloading paginated teachers list:', error);
    enhancedToast.error('Failed to download teachers list');
  }
};
